import { useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import HouseAccountsPanel from '../components/HouseAccountsPanel'
import MomentsPanel from '../components/MomentsPanel'
import TalkItOut from '../components/TalkItOut'
import RecapAutoDraftPanel from '../components/RecapAutoDraftPanel'
import WorkflowsPanel from '../components/WorkflowsPanel'
import ClipsTab from '../components/clips/ClipsTab'
import NewsletterTab from '../components/newsletter/NewsletterTab'
import NotesTab from '../components/notes/NotesTab'
import { useAuthStore } from '../store/authStore'
import CallPostGenerator from './CallPostGenerator'
import Content from './Content'
import CreateContent from './CreateContent'
import TopicGenerator from './TopicGenerator'

export type ContentTab = 'library' | 'create' | 'newsletter' | 'notes' | 'automation' | 'clips'

const TABS: { id: ContentTab; label: string; href: string; adminOnly?: boolean }[] = [
  { id: 'create', label: 'Create', href: '/create' },
  { id: 'library', label: 'Library', href: '/content' },
  // The creator's own email list and the issues sent to it.
  { id: 'newsletter', label: 'Newsletter', href: '/content/newsletter' },
  // The creator's own research and notes, searchable by question.
  { id: 'notes', label: 'Notes', href: '/content/notes' },
  // Workflows for every creator; the recap auto-draft schedule and
  // FinCreator's accounts below them for admins only.
  { id: 'automation', label: 'Automation', href: '/content/automation' },
  // Long video into short clips, through the Clip Factory service. Admin only for now.
  { id: 'clips', label: 'Clips', href: '/content/clips', adminOnly: true },
]

type CreateMode = 'topic' | 'trade'

const CREATE_MODES: { id: CreateMode; label: string }[] = [
  { id: 'topic', label: 'From a topic' },
  { id: 'trade', label: 'From a trade idea' },
]

/**
 * Create starts from what happened (Moments: calls locked and closed, today's
 * recap, a post that did well) and from what the creator says (Talk it out),
 * then has two inputs: a free-text topic for one
 * platform (the default), or a structured trade idea turned into posts for
 * several platforms. Opened
 * from a receipt (?call=<id>), it writes a longer post from that call.
 */
const CreateTab = () => {
  const [mode, setMode] = useState<CreateMode>('topic')
  // ?call=<id>: "Write a longer post" from that call's receipt.
  const callParam = useSearchParams()[0].get('call')
  const callId = callParam && /^\d+$/.test(callParam) ? Number(callParam) : null
  if (callId) return <CallPostGenerator callId={callId} />
  return (
    <div className="space-y-6">
      <MomentsPanel />
      <TalkItOut />
      <div role="group" aria-label="Create from" className="inline-flex rounded-md border border-gray-300 p-0.5">
        {CREATE_MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            aria-pressed={mode === m.id}
            onClick={() => setMode(m.id)}
            className={`rounded px-4 py-1.5 text-sm font-medium ${
              mode === m.id ? 'bg-primary-600 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      {mode === 'topic' ? <TopicGenerator /> : <CreateContent />}
    </div>
  )
}

/**
 * One sidebar item, six tabs: Create (a topic or a trade idea into posts),
 * Library (the posts themselves), Newsletter (the creator's email list and
 * issues), Notes (their research, searchable by question), Automation
 * (workflows; for admins also the daily recap auto-draft schedule and
 * FinCreator's accounts) and, for admins, Clips (long video into short clips). The URL picks
 * the tab, so /content opens Library, /create, kept for existing links, opens
 * Create, /content/automation opens Automation and /content/clips Clips.
 */
const ContentHub = ({ tab }: { tab: ContentTab }) => {
  const isAdmin = useAuthStore((state) => !!state.user?.is_superuser)
  const tabs = TABS.filter((t) => isAdmin || !t.adminOnly)
  if (!tabs.some((t) => t.id === tab)) return <Navigate to="/content" replace />

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Content</h1>

      {/* Six tabs are wider than a phone: the row scrolls, not the page. */}
      <div
        role="tablist"
        aria-label="Content"
        className="-mx-4 flex gap-1 overflow-x-auto border-b border-gray-200 px-4 sm:mx-0 sm:gap-2 sm:px-0"
      >
        {tabs.map((t) => {
          const selected = t.id === tab
          return (
            <Link
              key={t.id}
              to={t.href}
              role="tab"
              id={`content-tab-${t.id}`}
              aria-selected={selected}
              aria-controls="content-tabpanel"
              className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium sm:px-4 ${
                selected
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </Link>
          )
        })}
      </div>

      <div role="tabpanel" id="content-tabpanel" aria-labelledby={`content-tab-${tab}`}>
        {tab === 'create' ? (
          <CreateTab />
        ) : tab === 'automation' ? (
          <div className="space-y-6">
            <WorkflowsPanel />
            {isAdmin && <RecapAutoDraftPanel />}
            {isAdmin && <HouseAccountsPanel />}
          </div>
        ) : tab === 'clips' ? (
          <ClipsTab />
        ) : tab === 'newsletter' ? (
          <NewsletterTab />
        ) : tab === 'notes' ? (
          <NotesTab />
        ) : (
          <Content />
        )}
      </div>
    </div>
  )
}

export default ContentHub
